"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import useFramerMotion, { LazyMotion, m } from "@/hooks/useFramerMotion";
import { cn } from "@/utils";

// Cosmic orbit backdrop — replaces the (broken) lottie. ~7 organic squiggle
// "blobs" overlapping in the same halo, each a unique shape that slowly
// morphs/wobbles (Motion animates the path `d` between deterministic variants),
// plus a scatter of static dots that pulse on/off. Mint glow via drop-shadow.
// `m.*` needs a LazyMotion features provider (per-component pattern in this app),
// else nothing animates. Parent (FooterSpecial) already gates on !reduce.

const POINTS = 30;

// Deterministic wobbly closed loop around (0,0). Same point count across a line's
// phase variants so Motion can interpolate `d` between them (→ shape morph).
type Pt = [number, number];
function blobPoints(radius: number, ry: number, wobble: number, seed: number, phase: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < POINTS; i++) {
    const a = i / POINTS * Math.PI * 2;
    const n = Math.sin(a * 3 + seed + phase) * 0.5 + Math.sin(a * 5 - seed * 1.3 + phase * 0.7) * 0.3 + Math.sin(a * 2 + seed * 2.1 + phase * 1.3) * 0.2;
    const rr = radius * (1 + wobble * n);
    pts.push([Math.cos(a) * rr, Math.sin(a) * rr * ry]);
  }
  return pts;
}
function blobPath(radius: number, ry: number, wobble: number, seed: number, phase: number, curved = false): string {
  const pts = blobPoints(radius, ry, wobble, seed, phase);
  const N = pts.length;
  const f = (v: number): string => v.toFixed(1);
  if (!curved) {
    return pts.map((p, i) => `${i === 0 ? "M" : "L"}${f(p[0])} ${f(p[1])}`).join(" ") + " Z";
  }

  // Catmull-Rom → cubic Bézier, closed loop
  let d = `M${f(pts[0][0])} ${f(pts[0][1])} `;
  for (let i = 0; i < N; i++) {
    const p0 = pts[(i - 1 + N) % N];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % N];
    const p3 = pts[(i + 2) % N];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(p2[0])} ${f(p2[1])} `;
  }
  return `${d}Z`;
}
const LINES = [{
  r: 40,
  ry: 0.92,
  wob: 0.16,
  seed: 0.4,
  op: 0.5,
  dur: 16
}, {
  r: 46,
  ry: 0.78,
  wob: 0.2,
  seed: 1.7,
  op: 0.42,
  dur: 19
}, {
  r: 52,
  ry: 0.95,
  wob: 0.14,
  seed: 3.1,
  op: 0.36,
  dur: 22
}, {
  r: 44,
  ry: 0.7,
  wob: 0.22,
  seed: 4.5,
  op: 0.3,
  dur: 18
}, {
  r: 56,
  ry: 0.85,
  wob: 0.18,
  seed: 5.9,
  op: 0.26,
  dur: 25
}, {
  r: 50,
  ry: 0.66,
  wob: 0.24,
  seed: 7.3,
  op: 0.22,
  dur: 21
}, {
  r: 60,
  ry: 0.9,
  wob: 0.15,
  seed: 8.8,
  op: 0.18,
  dur: 28
}];

// Static dots (deterministic golden-angle scatter) that only pulse opacity.
const DOTS = Array.from({
  length: 13
}, (_, i) => {
  const a = i * 2.399963; // golden angle
  const rad = 22 + i * 37 % 54;
  return {
    x: +(Math.cos(a) * rad).toFixed(1),
    y: +(Math.sin(a) * rad * 0.82).toFixed(1),
    r: 0.6 + i % 3 * 0.2,
    accent: i % 3 === 0,
    dur: 2.2 + i % 5 * 0.7,
    delay: i % 7 * 0.5
  };
});
export default function SpaceOrbitWobble(t0) {
  const $ = _c(10);
  const {
    className
  } = t0;
  const framerMotionFeatures = useFramerMotion();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = LINES.map(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const lines = t1;
  let t2;
  if ($[1] !== className) {
    t2 = cn("origin-center", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      filter: "drop-shadow(0 0 4px rgba(0, 210, 154, 0.5))"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <svg viewBox="-80 -80 160 160" className="size-full overflow-visible" fill="none">{lines.map(_temp2)}{DOTS.map(_temp3)}</svg>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2) {
    t5 = <div aria-hidden="true" className={t2} style={t3}>{t4}</div>;
    $[5] = t2;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== framerMotionFeatures || $[8] !== t5) {
    t6 = <LazyMotion features={framerMotionFeatures}>{t5}</LazyMotion>;
    $[7] = framerMotionFeatures;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp3(dt, i_0) {
  return <m.circle key={i_0} cx={dt.x} cy={dt.y} r={dt.r} fill={dt.accent ? "var(--clr-accent)" : "var(--clr-mint)"} animate={{
    opacity: [0.12, 0.9, 0.12]
  }} transition={{
    duration: dt.dur,
    delay: dt.delay,
    ease: "easeInOut",
    repeat: Infinity
  }} />;
}
function _temp2(l_0, i) {
  return <m.path key={i} d={l_0.keyframes[0]} stroke="var(--clr-mint)" strokeOpacity={l_0.op} strokeWidth="0.6" animate={{
    d: l_0.keyframes
  }} transition={{
    duration: l_0.dur,
    ease: "easeInOut",
    repeat: Infinity
  }} />;
}
function _temp(l) {
  const isCurved = Math.random() > 0.5;
  return {
    op: l.op,
    dur: l.dur,
    keyframes: [blobPath(l.r, l.ry, l.wob, l.seed, 0, isCurved), blobPath(l.r, l.ry, l.wob, l.seed, 2.09, isCurved), blobPath(l.r, l.ry, l.wob, l.seed, 4.18, isCurved), blobPath(l.r, l.ry, l.wob, l.seed, 0, isCurved)]
  };
}